import { createContext, useContext, type ReactNode } from "react";

import type { Assessment, AssessmentId } from "@thalamos/haven-common";

import { useUser } from "../../Context/UserContext.js";
import { createRoute } from "../../routing/createRoute.js";

/**
 * Where the screens of an assessment lead. Each takes the assessment's id,
 * which a guest's paths ignore: a guest session reaches one assessment.
 */
export type AssessmentPaths = {
  overview: (assessmentId: AssessmentId) => string;
  domain: (assessmentId: AssessmentId, group: string) => string;
  item: (assessmentId: AssessmentId, group: string, item: string) => string;
  /** Where to go from an assessment that is no longer open; undefined where there is nowhere. */
  away: (assessment: Pick<Assessment, "personId">) => string | undefined;
};

/**
 * Where an assessment's screens lead, and whether whoever has them open is
 * the one answering it.
 */
export type AssessmentFrame = {
  paths: AssessmentPaths;
  /** Undefined while who has the screens open is still being read. */
  isOwner:
    ((assessment: Pick<Assessment, "ownerUserId">) => boolean) | undefined;
};

const staffAssessmentPaths: AssessmentPaths = {
  overview: createRoute.assessment,
  domain: createRoute.assessmentDomain,
  item: createRoute.assessmentItem,
  away: (assessment) => createRoute.person(assessment.personId),
};

const AssessmentFrameContext = createContext<AssessmentFrame | undefined>(
  undefined,
);

/** Gives the assessment screens inside a frame other than the staff app's. */
export const AssessmentFrameProvider = AssessmentFrameContext.Provider;

const StaffFrame = ({
  children,
}: {
  children: (frame: AssessmentFrame) => ReactNode;
}) => {
  const { user } = useUser();

  return children({
    paths: staffAssessmentPaths,
    isOwner:
      user === undefined
        ? undefined
        : (assessment) => assessment.ownerUserId === user.id,
  });
};

/**
 * Hands its children the frame an `AssessmentFrameProvider` above gives, or
 * the staff app's, where the signed-in user is the one who may answer.
 */
export const WithAssessmentFrame = ({
  children,
}: {
  children: (frame: AssessmentFrame) => ReactNode;
}) => {
  const given = useContext(AssessmentFrameContext);

  return given === undefined ? (
    <StaffFrame>{children}</StaffFrame>
  ) : (
    children(given)
  );
};
